import { useEffect, useState } from "react";
import { Users, UserCheck, Bot, Circle, Shield, Hash, Volume2, Megaphone, FolderTree, Rocket, RefreshCw, Crown, Loader2, ArrowUpRight, ShieldAlert, Zap, Trophy, Gift, Sparkles, SquareSlash } from "lucide-react";
import { Link } from "react-router-dom";
import { InitialSyncBanner } from "@/components/InitialSync";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function fmtDate(s) { return s ? new Date(s).toLocaleString("it-IT") : "mai"; }

const QUICK = [
  { to: "/moderazione", title: "Moderazione", text: "Sanzioni, AutoMod, anti-raid e lockdown", icon: ShieldAlert, gradient: "linear-gradient(135deg,#4F46E5 0%,#6366F1 45%,#8B5CF6 100%)" },
  { to: "/automazioni", title: "Automazioni", text: "Benvenuto, ticket, log e messaggi programmati", icon: Zap, gradient: "linear-gradient(135deg,#0EA5E9 0%,#3B82F6 60%,#6366F1 100%)" },
  { to: "/livelli", title: "Livelli ed economia", text: "XP, classifica, ruoli premio e monete", icon: Trophy, gradient: "linear-gradient(135deg,#A21CAF 0%,#C026D3 50%,#E879F9 100%)" },
  { to: "/giveaway", title: "Giveaway e sondaggi", text: "Premi con estrazione automatica", icon: Gift, gradient: "linear-gradient(135deg,#F97316 0%,#EF4444 55%,#EC4899 100%)" },
  { to: "/canali-auto", title: "Canali intelligenti", text: "Vocali temporanei, contatori, thread", icon: Sparkles, gradient: "linear-gradient(135deg,#059669 0%,#10B981 50%,#22D3EE 100%)" },
  { to: "/comandi", title: "Comandi slash", text: "51 comandi, attivabili uno per uno", icon: SquareSlash, gradient: "linear-gradient(135deg,#334155 0%,#475569 55%,#64748B 100%)" },
];

function Card({ title, icon: Icon, color, children }) {
  return (
    <div className="card-elevated p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${color}1f`, boxShadow: `0 0 16px -6px ${color}88` }}><Icon className="h-4.5 w-4.5" style={{ color }} /></div>
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}
function Row({ label, value, mono = true, color }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`text-sm font-bold ${mono ? "font-mono" : ""}`} style={{ color: color || "#fff" }}>{value}</span>
    </div>
  );
}

export default function Overview() {
  const { refresh } = useData();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState(null);

  const load = async () => { try { setD(await api.overview()); } catch (e) { setErr(apiError(e)); } };
  useEffect(() => { load(); }, []);
  // Si aggiorna da sola quando arriva una modifica da Discord (ruoli, canali, membri, server).
  useEffect(() => {
    let t;
    const onLive = (e) => {
      if (/^(role|channel|member|guild|site\.refresh)/.test(e.detail?.event || "")) { clearTimeout(t); t = setTimeout(load, 800); }
    };
    window.addEventListener("kalyx:live", onLive);
    return () => { window.removeEventListener("kalyx:live", onLive); clearTimeout(t); };
  }, []);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await api.sync();
      setReport(r.report); setConfirm(false);
      toast.success("Sincronizzazione completata");
      await load(); await refresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  if (!d && !err) return (
    <div className="space-y-6">
      <Skeleton className="h-20 w-full rounded-xl bg-white/5" />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">{[0,1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl bg-white/5" />)}</div>
    </div>
  );
  if (err) return <div className="rounded-xl border border-[#F23F43]/30 bg-[#F23F43]/10 px-4 py-3 text-sm text-[#F23F43]">{err}</div>;

  const g = d.guild || {}; const m = d.members; const r = d.roles; const c = d.channels; const b = d.boost;
  const online = d.health?.ok || d.health?.ready;
  const nextLevel = { 0: 2, 1: 7, 2: 14, 3: 14 }[b.level ?? 0] || 14;
  const boostPct = Math.min(100, Math.round(((b.count || 0) / nextLevel) * 100));

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="card-elevated flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
        {g.icon ? <img src={g.icon} alt="" className="h-16 w-16 rounded-2xl object-cover ring-2 ring-white/10" />
          : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5865F2] to-[#8B5CF6]"><Bot className="h-7 w-7 text-white" /></div>}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-extrabold tracking-tight text-white sm:text-3xl" data-testid="overview-guild-name">{g.name || "—"}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
            <span className="font-mono">ID {g.id || "—"}</span>
            <span className="flex items-center gap-1"><Crown className="h-3.5 w-3.5 text-[#FEE75C]" /> {g.ownerName || "—"}</span>
            {b.level != null && <span className="flex items-center gap-1"><Rocket className="h-3.5 w-3.5 text-[#F47FFF]" /> Livello boost {b.level}</span>}
            <span className={`flex items-center gap-1 font-semibold ${online ? "text-[#23A55A]" : "text-[#F23F43]"}`}><span className={`h-1.5 w-1.5 rounded-full ${online ? "bg-[#23A55A]" : "bg-[#F23F43]"}`} /> Bot {online ? "online" : "offline"}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">Ultima sincronizzazione: <span className="font-mono text-slate-400" data-testid="last-sync">{fmtDate(d.lastSync)}</span></p>
        </div>
        <Button onClick={() => setConfirm(true)} data-testid="sync-server-button"
          className="rounded-xl bg-gradient-to-r from-[#5865F2] to-[#7289DA] px-5 py-6 text-base font-bold text-white shadow-lg shadow-[#5865F2]/30 transition-all hover:from-[#4752C4] hover:to-[#5865F2] active:scale-[0.98]">
          <RefreshCw className="mr-2 h-5 w-5" /> Sincronizza Server
        </Button>
      </div>

      <InitialSyncBanner />

      {report && (
        <div className="card-elevated animate-fade-up border-[#23A55A]/30 p-5" data-testid="sync-report">
          <p className="mb-2 font-bold text-[#23A55A]">✓ Sincronizzazione completata</p>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-6">
            <div><p className="font-mono text-xl font-bold text-white">{report.membersUpdated}</p><p className="text-xs text-slate-400">membri</p></div>
            <div><p className="font-mono text-xl font-bold text-white">{report.rolesFound}</p><p className="text-xs text-slate-400">ruoli</p></div>
            <div><p className="font-mono text-xl font-bold text-white">{report.channelsFound}</p><p className="text-xs text-slate-400">canali</p></div>
            <div><p className="font-mono text-xl font-bold text-white">{report.categoriesFound}</p><p className="text-xs text-slate-400">categorie</p></div>
            <div><p className="font-mono text-xl font-bold text-[#5865F2]">{report.changesDetected}</p><p className="text-xs text-slate-400">modifiche</p></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Card title="Membri" icon={Users} color="#5865F2">
          <Row label="Totali" value={m.total} /><Row label="Utenti reali" value={m.real} color="#8ea0ff" />
          <Row label="Bot" value={m.bots} /><Row label="Online" value={m.online} color="#23A55A" />
          <Row label="Offline" value={m.offline} color="#949BA4" /><Row label="Entrati (7g)" value={m.recent} color="#00F0FF" />
        </Card>
        <Card title="Ruoli" icon={Shield} color="#8B5CF6">
          <Row label="Totali" value={r.total} /><Row label="Utilizzati" value={r.used} color="#23A55A" />
          <Row label="Senza membri" value={r.empty} color="#FEE75C" />
          <Row label="Più usato" value={r.top ? `${r.top.name} (${r.top.count})` : "—"} mono={false} color={r.top?.color} />
        </Card>
        <Card title="Canali" icon={Hash} color="#00F0FF">
          <Row label="Totali" value={c.total} /><Row label="Testuali" value={c.text} />
          <Row label="Vocali" value={c.voice} /><Row label="Annunci" value={c.announcement} />
          <Row label="Forum" value={c.forum} /><Row label="Stage" value={c.stage} />
        </Card>
        <div className="space-y-5">
          <Card title="Categorie" icon={FolderTree} color="#F0B232"><Row label="Totali" value={d.categories.total} /></Card>
          <Card title="Boost" icon={Rocket} color="#F47FFF">
            <Row label="Livello" value={b.level ?? "—"} /><Row label="Boost attivi" value={b.count ?? "—"} color="#F47FFF" />
          </Card>
        </div>
      </div>

      {/* Boost dettaglio */}
      <div className="card-elevated p-5">
        <div className="mb-4 flex items-center gap-2.5"><Rocket className="h-5 w-5 text-[#F47FFF]" /><h3 className="text-base font-bold text-white">Boost del server</h3></div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm"><span className="text-slate-400">Progresso verso livello {(b.level ?? 0) + 1}</span><span className="font-mono text-white">{b.count ?? 0}/{nextLevel}</span></div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-[#0B0C10]"><div className="h-full rounded-full bg-gradient-to-r from-[#F47FFF] to-[#5865F2] transition-all" style={{ width: `${boostPct}%` }} /></div>
            <p className="mt-2 text-xs text-slate-500">Le informazioni boost mostrate sono quelle esposte dalle API Discord.</p>
          </div>
          {(b.available != null) && (
            <div className="rounded-xl border border-white/10 bg-[#0B0C10] p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Pool boost (gestione interna)</p>
              <div className="flex items-center gap-6">
                <div><p className="font-mono text-2xl font-bold text-white">{b.available}</p><p className="text-xs text-slate-400">disponibili</p></div>
                <div><p className="font-mono text-2xl font-bold text-[#F47FFF]">{b.used}</p><p className="text-xs text-slate-400">utilizzati</p></div>
                <div><p className="font-mono text-2xl font-bold text-[#23A55A]">{(b.available || 0) - (b.used || 0)}</p><p className="text-xs text-slate-400">liberi</p></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Accesso rapido alle funzioni */}
      <div>
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Accesso rapido</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {QUICK.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`quick-${q.to.slice(1)}`}
              className="group relative overflow-hidden rounded-2xl p-5 text-white shadow-[0_14px_34px_-18px_rgba(0,0,0,0.9)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_44px_-18px_rgba(0,0,0,0.95)]"
              style={{ background: q.gradient }}>
              <q.icon className="absolute -bottom-5 -right-4 h-28 w-28 text-white/15 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" strokeWidth={1.4} />
              <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur"><q.icon className="h-5 w-5" /></div>
              <p className="relative mt-4 flex items-center gap-1 text-lg font-extrabold tracking-tight">{q.title}<ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" /></p>
              <p className="relative mt-0.5 text-sm text-white/80">{q.text}</p>
            </Link>
          ))}
        </div>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="border-white/10 bg-[#12141D] text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Vuoi sincronizzare nuovamente il server?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Il bot eseguirà una scansione completa (membri, ruoli, canali, categorie, permessi, boost) e aggiornerà la cache del sito.</p>
          <p className="text-sm text-slate-500">Ultima sincronizzazione: <span className="font-mono">{fmtDate(d.lastSync)}</span></p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(false)} className="text-slate-400">Annulla</Button>
            <Button onClick={doSync} disabled={syncing} data-testid="sync-confirm-button" className="bg-[#5865F2] hover:bg-[#4752C4]">
              {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Sincronizza ora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
